define('home/views/ProfileView',[
    'underscore',
    'backbone-marionette',

    'core/api',
    'core/strings',

    'home/utils/backboneUtils',

    'home/views/common/TabbedView',
    'home/views/ProfileCoverView',
    'home/views/ProfileCardCollectionView',
    'home/views/ProfileCommentsEmptyView',
    'home/views/ProfileFavoritesEmptyView',
    'home/views/ProfileFollowersEmptyView',
    'home/views/ProfileRecentEmptyView',
    'home/views/ProfileUserModuleView',
    'home/views/ProfileModerationDetailsModuleView',
    'home/views/common/FetchableCollectionLayout',
    'home/views/ProfileFollowingView',
    'home/views/FollowerCollectionView',
    'home/views/ProfileRecommendedModuleView',
    'home/views/ProfileCommentsModuleView',
    'home/views/ProfileModuleEmptyView',
    'home/views/UserActiveForumsModuleView',

    'home/templates/blockedProfile',
    'home/templates/profileView',
    'home/templates/spinnerTall',
    'home/templates/privateProfile',
    'home/templates/profileAlertBlockError',
    'home/templates/profileAlertPrivate',

    'home/mixins/withClickLinkTracking',
    'home/mixins/withEmailVerificationAlert',
    'home/mixins/withLoginOverlay',
    'home/models/Session',
], function (
    _,
    Marionette,

    api,
    strings,

    backboneUtils,

    TabbedView,
    ProfileCoverView,
    ProfileCardCollectionView,
    ProfileCommentsEmptyView,
    ProfileFavoritesEmptyView,
    ProfileFollowersEmptyView,
    ProfileRecentEmptyView,
    ProfileUserModuleView,
    ProfileModerationDetailsModuleView,
    FetchableCollectionLayout,
    ProfileFollowingView,
    FollowerCollectionView,
    ProfileRecommendedModuleView,
    ProfileCommentsModuleView,
    ProfileModuleEmptyView,
    UserActiveForumsModuleView,

    blockedProfileTemplate,
    profileViewTemplate,
    spinnerTallTemplate,
    privateProfileTemplate,
    alertBlockErrorTemplate,
    alertPrivateTemplate,

    withClickLinkTracking,
    withEmailVerificationAlert,
    withLoginOverlay,
    Session
) {
    'use strict';

    var gettext = strings.get;

    var ProfileView = TabbedView.extend({
        defaultTab: 'comments',

        /**
         * Determines which template to show. If the user has not been
         * fetched, shows a loading icon. Otherwise renders the profile
         * view.
         * This is done because both the feed area and the cover area show
         * spinners while loading their respective content, and 2 spinners on
         * the page looks weird.
         * @param  {Object} data - JSON representation of this view's model
         * @returns {function}    - The function to return the html for this view
         */
        template: function (data) {
            if (!data.user.name)
                return spinnerTallTemplate;

            return profileViewTemplate(data);
        },

        templateHelpers: function () {
            return {
                isSessionUser: this.model.user.isSessionUser(),
            };
        },

        regions: _.defaults({
            coverRegion: '[data-role=cover-area]',
            userModuleRegion: '[data-role=user-module]',
            recommendedModuleRegion: '[data-role=recommended-module]',
            commentsModuleRegion: '[data-role=comments-module]',
            alertRegion: '[data-role=alert-message]',
            activeForumsModuleRegion: '[data-role=active-forums-module]',
            moderationModuleRegion: '[data-role=moderation-module]',
        }, TabbedView.prototype.regions),

        events: _.defaults({
            'click a[data-link-name]': 'trackClickLink',
        }, TabbedView.prototype.events),

        initialize: function (options) {
            TabbedView.prototype.initialize.apply(this, arguments);

            this.listenTo(this.model.user, 'change:isBlocked sync', this.render);
            this.subTab = options && options.subTab;
            this.session = Session.get();

            this.listenTo(this, 'change:activeTab', this.updateTabSpecificModules);

            this.listenTo(this.model, 'moderationDetailsReady', this.onDomRefresh);
            this.listenTo(this.model.user, 'blockError', this.onUserBlockError);
            this.listenTo(this.model.user, 'reportError', this.onUserReportError);
        },

        getMetaAttrs: function () {
            var user = this.model.user;
            return backboneUtils.getPromiseFor(user, 'name')
                .then(function (userDisplayName) {
                    return {
                        type: 'profile',
                        title: strings.interpolate(gettext('%(name)s · Profile'), { name: userDisplayName }),
                        description: _.escape(user.get('about')),
                        image: user.has('avatar') && user.get('avatar').cache,
                    };
                });
        },

        updateTabContent: function () {
            if (this.model.isBlocked()) {
                this.contentRegion.show(new Marionette.ItemView({
                    template: blockedProfileTemplate,
                }));
            } else if (this.model.isPrivate() && !this.session.user.isGlobalAdmin()) {
                this.contentRegion.show(new Marionette.ItemView({
                    template: privateProfileTemplate,
                }));

                // Triggers the 'Deal With It' animation for the private profile (can't be triggered through CSS)
                var self = this;
                _.defer(function () {
                    self.$('.empty-message-private').addClass('loaded');
                });
            } else {
                TabbedView.prototype.updateTabContent.apply(this, arguments);
            }
        },

        /*
         * Shows and hides modules that are specific to a particular tab based on what
         * the current active tab is.
         */
        updateTabSpecificModules: function (activeTab) {
            // No modules are available if the user is private so return here if so.
            if (this.model.isPrivate() || this.model.isBlocked())
                return;

            if (activeTab === 'comments') {
                this.hideRegion(this.commentsModuleRegion);
                this.recommendedModuleRegion.$el.show();
            } else {
                if (activeTab === 'favorites')
                    this.hideRegion(this.recommendedModuleRegion);
                else
                    this.recommendedModuleRegion.$el.show();

                this.commentsModuleRegion.$el.show();

                // Re-render module so truncation occurs when browsing through different profile pages and tabs
                this.commentsModuleRegion.currentView.render();
            }
        },

        hideRegion: function (region) {
            if (region.$el)
                region.$el.hide();
        },

        showProfileModerationModule: function () {
            this.moderationModuleRegion.show(
                new ProfileModerationDetailsModuleView({ model: this.model.moderationDetails })
            );
        },

        onDomRefresh: function () {
            if (this.$(this.coverRegion.el).length)
                this.coverRegion.show(new ProfileCoverView({ model: this.model.user }));

            if (!this.model.isBlocked()) {
                var isGlobalAdmin = this.session.user.isGlobalAdmin();

                this.userModuleRegion.show(new ProfileUserModuleView({ model: this.model.user }));

                if (!this.model.isPrivate() || isGlobalAdmin) {
                    this.recommendedModuleRegion.show(new ProfileRecommendedModuleView({
                        model: this.model.user,
                        collection: this.model.favorites.items,
                        emptyView: ProfileModuleEmptyView,
                    }));

                    this.commentsModuleRegion.show(new ProfileCommentsModuleView({
                        model: this.model.user,
                        collection: this.model.posts.items,
                        emptyView: ProfileModuleEmptyView,
                    }));

                    this.activeForumsModuleRegion.show(new UserActiveForumsModuleView({
                        model: this.model.mostActiveForums,
                        collection: this.model.mostActiveForums.items,
                        emptyView: ProfileModuleEmptyView,
                    }));
                }

                // Display indicator that let NetMods know their displaying a private profile
                if (this.model.isPrivate() && isGlobalAdmin) {
                    this.alertRegion.show(new Marionette.ItemView({
                        model: this.model.user,
                        template: alertPrivateTemplate,
                    }));
                }

                if (isGlobalAdmin)
                    this.showProfileModerationModule();
            }

            TabbedView.prototype.onDomRefresh.apply(this, arguments);
        },

        onUserBlockError: function (xhr) {
            var errorCode = xhr && xhr.responseJSON && xhr.responseJSON.code;
            this.alertRegion.show(new Marionette.ItemView({
                model: this.model.user,
                templateHelpers: {
                    isLimitError: errorCode === api.ERROR_CODES.MAX_ITEMS_REACHED,
                },
                template: alertBlockErrorTemplate,
            }));
        },

        onUserReportError: function () {
            this.alertRegion.show(new Marionette.ItemView({
                template: alertBlockErrorTemplate,
            }));
        },

        getTrackingSection: function (activeTab) {
            switch (activeTab) {
            case 'following':
                return 'following';
            case 'followers':
                return 'followers';
            case 'comments':
                return 'comments';
            case 'favorites':
                return 'favorites';
            case 'discussions':
                return 'discussions';
            }

            // Main comments feed is default, return nothing
        },

        getTabView: function (activeTab) {
            var view;

            switch (activeTab) {
            case 'following':
                view = new ProfileFollowingView({
                    model: this.model.user,
                    userProfile: this.model,
                    activeTab: this.subTab,
                });
                view.$el.attr({
                    'class': 'card-wrap',
                });

                // Propagate badTab event up to be handled by the router
                this.listenTo(view, 'badTab', _.bind(this.trigger, this, 'badTab'));

                return view;
            case 'followers':
                view = new FetchableCollectionLayout({
                    user: this.model.user,
                    model: this.model.followers,
                    collection: this.model.followers.get('items'),
                    fetchableCollectionView: FollowerCollectionView,
                    emptyView: ProfileFollowersEmptyView,
                    infiniteScroll: true,
                });
                view.$el.attr({
                    'class': 'card-wrap',
                });

                return view;
            case 'comments':
                return this.getProfileCardCollectionView(
                    this.model.user,
                    this.model.posts,
                    ProfileCommentsEmptyView
                );
            case 'favorites':
                return this.getProfileCardCollectionView(
                    this.model.user,
                    this.model.favorites,
                    ProfileFavoritesEmptyView
                );
            case 'discussions':
                return this.getProfileCardCollectionView(
                    this.model.user,
                    this.model.threads,
                    ProfileRecentEmptyView
                );
            case 'recent':
                return this.getProfileCardCollectionView(
                    this.model.user,
                    this.model.profileFeed,
                    ProfileRecentEmptyView
                );
            }
        },

        getProfileCardCollectionView: function (user, collection, emptyView) {
            return new FetchableCollectionLayout({
                model: collection,
                collection: collection.get('items'),
                user: user,
                fetchableCollectionView: ProfileCardCollectionView,
                emptyView: emptyView,
                infiniteScroll: true,
            });
        },
    });

    withClickLinkTracking.call(ProfileView.prototype);
    withEmailVerificationAlert.call(ProfileView.prototype);
    withLoginOverlay.call(ProfileView.prototype,
        strings.interpolate(
            gettext('Join %(disqus)s to get your own profile like this.'),
            { disqus: 'Disqus' }
        ));

    return ProfileView;
});

